import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Send, Paperclip, ArrowLeft, MessageSquare, Loader2, Trash2,
  Check, CheckCheck, Download, FileText, X, Image as ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth, API } from "@/App";

/**
 * Unified ChatPanel - works for both brand and star sides.
 *
 * Props:
 *  - accentColor (optional): tailwind-safe hex, default '#7B2FBE'
 *  - initialThreadId (optional): open this thread directly
 *  - initialRefId (optional): open/create thread from a collab_request/booking_id
 *  - initialOtherUserId (optional): open/create thread with this user id
 *  - onThreadOpened (optional): callback(threadId)
 *  - onBack (optional): called when user clicks back from thread view (if provided, shows back button)
 *  - emptyTitle, emptySubtitle (optional)
 */
export default function ChatPanel({
  accentColor = "#7B2FBE",
  initialThreadId = null,
  initialRefId = null,
  initialOtherUserId = null,
  onThreadOpened = null,
  onBack = null,
  emptyTitle = "No conversations yet",
  emptySubtitle = "Start a chat from your deals or requests.",
}) {
  const { user, getAuthHeaders } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const openedInitialRef = useRef(false);

  // ========== API helpers ==========
  const fetchThreads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/threads`, { headers: getAuthHeaders() });
      setThreads(res.data.threads || []);
    } catch (e) {
      console.error("fetchThreads failed", e);
    } finally {
      setLoadingThreads(false);
    }
  }, [getAuthHeaders]);

  const fetchMessages = useCallback(async (threadId, silent = false) => {
    if (!silent) setLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/chat/threads/${threadId}/messages`, {
        headers: getAuthHeaders(),
      });
      setMessages(res.data.messages || []);
    } catch (e) {
      if (!silent) {
        console.error("fetchMessages failed", e);
        setMessages([]);
      }
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, [getAuthHeaders]);

  const markRead = useCallback(async (threadId) => {
    try {
      await axios.put(`${API}/chat/threads/${threadId}/read`, {}, { headers: getAuthHeaders() });
      window.dispatchEvent(new Event("messagesRead"));
    } catch { /* silent */ }
  }, [getAuthHeaders]);

  const openThreadById = useCallback(async (threadId) => {
    const t = threads.find(x => x.thread_id === threadId);
    if (t) {
      setActiveThread(t);
      await fetchMessages(threadId);
      await markRead(threadId);
      if (onThreadOpened) onThreadOpened(threadId);
    } else {
      // Refetch list first
      try {
        const res = await axios.get(`${API}/chat/threads`, { headers: getAuthHeaders() });
        const list = res.data.threads || [];
        setThreads(list);
        const found = list.find(x => x.thread_id === threadId);
        if (found) {
          setActiveThread(found);
          await fetchMessages(threadId);
          await markRead(threadId);
          if (onThreadOpened) onThreadOpened(threadId);
        }
      } catch (e) { console.error(e); }
    }
  }, [threads, fetchMessages, markRead, onThreadOpened, getAuthHeaders]);

  const openOrCreateThread = useCallback(async ({ ref_id, other_user_id }) => {
    try {
      const res = await axios.post(
        `${API}/chat/threads/open`,
        { ref_id: ref_id || null, other_user_id: other_user_id || null },
        { headers: getAuthHeaders() }
      );
      const t = res.data;
      setActiveThread(t);
      await fetchMessages(t.thread_id);
      await markRead(t.thread_id);
      await fetchThreads();
      if (onThreadOpened) onThreadOpened(t.thread_id);
    } catch (e) {
      console.error("openOrCreateThread failed", e);
      toast.error(e?.response?.data?.detail || "Failed to open chat");
    }
  }, [getAuthHeaders, fetchMessages, markRead, fetchThreads, onThreadOpened]);

  // ========== Initial load ==========
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Handle initial props once threads loaded (or directly for open)
  useEffect(() => {
    if (openedInitialRef.current) return;
    if (loadingThreads) return;
    if (initialThreadId) {
      openedInitialRef.current = true;
      openThreadById(initialThreadId);
    } else if (initialRefId) {
      openedInitialRef.current = true;
      openOrCreateThread({ ref_id: initialRefId });
    } else if (initialOtherUserId) {
      openedInitialRef.current = true;
      openOrCreateThread({ other_user_id: initialOtherUserId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingThreads, initialThreadId, initialRefId, initialOtherUserId]);

  // ========== Polling ==========
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeThread) {
        fetchMessages(activeThread.thread_id, true);
      } else {
        fetchThreads();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [activeThread, fetchMessages, fetchThreads]);

  // ========== Auto-scroll ==========
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, activeThread]);

  // ========== Actions ==========
  const handleSend = async () => {
    if (!activeThread) return;
    if (!text.trim() && !selectedFile) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("content", text || "");
      if (selectedFile) form.append("file", selectedFile);
      const res = await axios.post(
        `${API}/chat/threads/${activeThread.thread_id}/messages`,
        form,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      // Append optimistically
      if (res.data?.message) {
        setMessages(prev => [...prev, res.data.message]);
      }
      if (Array.isArray(res.data?.warnings) && res.data.warnings.length > 0) {
        toast.warning(res.data.warnings[0], { duration: 5000 });
      }
      setText("");
      setSelectedFile(null);
      fetchThreads();
    } catch (e) {
      console.error("send failed", e);
      toast.error(e?.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isVideo = f.type.startsWith("video/");
    const maxBytes = isVideo ? 100 * 1024 * 1024 : 25 * 1024 * 1024;
    if (f.size > maxBytes) {
      toast.error(`File too large. Max ${maxBytes / (1024 * 1024)}MB`);
      return;
    }
    setSelectedFile(f);
    e.target.value = "";
  };

  const handleDelete = async (messageId) => {
    if (!confirm("Delete this message?")) return;
    setDeletingId(messageId);
    try {
      await axios.delete(`${API}/chat/messages/${messageId}`, { headers: getAuthHeaders() });
      setMessages(prev => prev.map(m => m.message_id === messageId
        ? { ...m, is_deleted: true, content: "This message was deleted", attachment_url: null, attachment_type: null }
        : m));
    } catch (e) {
      toast.error("Failed to delete message");
    } finally {
      setDeletingId(null);
    }
  };

  const backToInbox = () => {
    setActiveThread(null);
    setMessages([]);
    fetchThreads();
    if (onBack) onBack();
  };

  // ========== Helpers ==========
  const getFileUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    // url already contains /api/files/... ; strip /api since API already ends with /api
    if (url.startsWith("/api/")) return `${API}${url.slice(4)}`;
    return `${API}/files/${url}`;
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const sameDay = d.toDateString() === now.toDateString();
      if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      return d.toLocaleDateString([], { day: "numeric", month: "short" });
    } catch { return ""; }
  };

  const filePreview = useMemo(() => {
    if (!selectedFile) return null;
    if (selectedFile.type.startsWith("image/")) {
      return URL.createObjectURL(selectedFile);
    }
    return null;
  }, [selectedFile]);

  // ========== Render ==========
  if (loadingThreads && !activeThread) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: accentColor }} />
      </div>
    );
  }

  // Thread view
  if (activeThread) {
    const other = activeThread.other_user || {};
    return (
      <div className="flex flex-col bg-white rounded-2xl shadow-sm border overflow-hidden" style={{ height: "min(70vh, 640px)" }}>
        {/* Header */}
        <div className="flex items-center gap-3 p-3 border-b bg-white sticky top-0 z-10">
          <Button variant="ghost" size="icon" onClick={backToInbox} className="shrink-0" data-testid="chat-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Avatar className="w-10 h-10">
            {other.avatar && <AvatarImage src={other.avatar.startsWith("http") ? other.avatar : getFileUrl(other.avatar)} />}
            <AvatarFallback className="text-white" style={{ backgroundColor: accentColor }}>
              {(other.name || "U").charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1E293B] truncate">{other.name || "User"}</p>
            <p className="text-xs text-[#94A3B8]">Chat</p>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8FAFC]">
          {loadingMessages ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: accentColor }} />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-[#94A3B8]">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No messages yet. Say hi!</p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_id === user?.user_id;
              const isDeleted = m.is_deleted;
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"} group`}>
                  <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 relative ${mine ? "text-white rounded-br-sm" : "bg-white text-[#1E293B] rounded-bl-sm shadow-sm border"}`}
                       style={mine ? { backgroundColor: accentColor } : {}}>
                    {mine && !isDeleted && (
                      <button
                        onClick={() => handleDelete(m.message_id)}
                        disabled={deletingId === m.message_id}
                        className="absolute -left-7 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded-full"
                        title="Delete"
                      >
                        {deletingId === m.message_id ? (
                          <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        )}
                      </button>
                    )}

                    {/* Attachment */}
                    {m.attachment_url && !isDeleted && (
                      <div className="mb-1.5">
                        {m.attachment_type === "image" ? (
                          <div className="relative">
                            <img
                              src={getFileUrl(m.attachment_url)}
                              alt={m.attachment_name || "image"}
                              className="max-w-full rounded-lg cursor-pointer max-h-72 object-cover"
                              onClick={() => window.open(getFileUrl(m.attachment_url), "_blank")}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect fill='%23eee' width='200' height='150'/%3E%3Ctext x='50%25' y='50%25' fill='%23999' font-family='sans-serif' font-size='14' text-anchor='middle' dominant-baseline='middle'%3EImage%3C/text%3E%3C/svg%3E";
                              }}
                            />
                          </div>
                        ) : m.attachment_type === "video" ? (
                          <video src={getFileUrl(m.attachment_url)} controls className="max-w-full rounded-lg max-h-72" />
                        ) : (
                          <a href={getFileUrl(m.attachment_url)} target="_blank" rel="noopener noreferrer"
                             className={`flex items-center gap-2 p-2 rounded-lg ${mine ? "bg-white/20" : "bg-[#F1F5F9]"}`}>
                            <FileText className="w-5 h-5" />
                            <span className="text-sm truncate max-w-[180px]">{m.attachment_name || "Document"}</span>
                            <Download className="w-4 h-4 ml-auto shrink-0" />
                          </a>
                        )}
                      </div>
                    )}

                    {m.content && (
                      <p className={`text-sm whitespace-pre-wrap break-words ${isDeleted ? "italic opacity-70" : ""}`}>
                        {m.content}
                      </p>
                    )}

                    <div className={`flex items-center justify-end gap-1 mt-0.5 ${mine ? "text-white/70" : "text-[#94A3B8]"}`}>
                      <span className="text-[10px]">{formatTime(m.created_at)}</span>
                      {mine && !isDeleted && (
                        <span title={m.is_read ? "Seen" : "Sent"}>
                          {m.is_read ? <CheckCheck className="w-3.5 h-3.5" /> : <Check className="w-3 h-3" />}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Selected file preview */}
        {selectedFile && (
          <div className="px-3 py-2 border-t bg-[#F8FAFC] flex items-center gap-2">
            {filePreview ? (
              <img src={filePreview} alt="preview" className="w-12 h-12 object-cover rounded" />
            ) : (
              <div className="w-12 h-12 rounded bg-white border flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#64748B]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate text-[#1E293B]">{selectedFile.name}</p>
              <p className="text-xs text-[#94A3B8]">{(selectedFile.size / 1024).toFixed(1)} KB</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setSelectedFile(null)} className="h-7 w-7">
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Composer */}
        <div className="p-3 border-t bg-white flex gap-2 items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="chat-file-input"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            className="text-[#64748B]"
            data-testid="chat-attach-btn"
          >
            <Paperclip className="w-5 h-5" />
          </Button>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message..."
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={sending}
            className="flex-1 rounded-full"
            data-testid="chat-message-input"
          />
          <Button
            onClick={handleSend}
            disabled={sending || (!text.trim() && !selectedFile)}
            className="rounded-full px-4"
            style={{ backgroundColor: accentColor }}
            data-testid="chat-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    );
  }

  // Inbox view
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="p-4 border-b">
        <h3 className="font-semibold text-[#1E293B] flex items-center gap-2">
          <MessageSquare className="w-5 h-5" style={{ color: accentColor }} />
          Chats
        </h3>
      </div>
      {threads.length === 0 ? (
        <div className="text-center py-12 px-6">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
          <p className="text-[#64748B] font-medium">{emptyTitle}</p>
          <p className="text-sm text-[#94A3B8] mt-1">{emptySubtitle}</p>
        </div>
      ) : (
        <div className="divide-y">
          {threads.map((t) => {
            const other = t.other_user || {};
            const hasUnread = (t.unread_count || 0) > 0;
            return (
              <button
                key={t.thread_id}
                onClick={() => openThreadById(t.thread_id)}
                className={`w-full p-3 flex items-center gap-3 hover:bg-[#F8FAFC] transition-colors text-left ${hasUnread ? "bg-[#FFF7FB]" : ""}`}
                data-testid={`chat-thread-${t.thread_id}`}
              >
                <Avatar className="w-11 h-11 shrink-0">
                  {other.avatar && <AvatarImage src={other.avatar.startsWith("http") ? other.avatar : getFileUrl(other.avatar)} />}
                  <AvatarFallback className="text-white" style={{ backgroundColor: accentColor }}>
                    {(other.name || "U").charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`truncate ${hasUnread ? "font-bold text-[#1E293B]" : "font-medium text-[#1E293B]"}`}>
                      {other.name || "User"}
                    </p>
                    <span className="text-xs text-[#94A3B8] shrink-0">
                      {formatTime(t.last_message_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className={`text-sm truncate ${hasUnread ? "text-[#1E293B] font-semibold" : "text-[#64748B]"}`}>
                      {t.last_message || "Start the conversation..."}
                    </p>
                    {hasUnread && (
                      <span className="min-w-[18px] h-[18px] px-1.5 text-xs font-bold rounded-full flex items-center justify-center text-white shrink-0"
                            style={{ backgroundColor: accentColor }}>
                        {t.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
